import { DateTime } from 'luxon';
import type { WorkLogEditor } from '../../services/api/types.gen';
import {
  formatDateInAgencyZone,
  formatDateTimeInAgencyZone,
  formatDateToMMDDYYYY,
  formatTimeInAgencyZone,
} from '../../utils/dateUtils';
import type { WorkLogEntry } from './types';

/**
 * A record's own display rule for one field's value: an enum code's label, a
 * money amount. Return `undefined` to fall back to the shared formatting.
 */
export type WorkLogValueFormatter = (
  field: string,
  value: unknown
) => string | undefined;

const DATE_ONLY = /^\d{4}-\d{2}-\d{2}$/;
const DATE_TIME = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/;

/** "kx_modifier" -> "Kx modifier"; fieldLabels wins when it names the field. */
export const humanizeField = (
  field: string,
  fieldLabels?: Record<string, string>
): string => {
  const mapped = fieldLabels?.[field];
  if (mapped) return mapped;
  const spaced = field.replace(/_/g, ' ');
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
};

const formatScalar = (value: unknown): string | null => {
  if (value === null || value === undefined || value === '') return null;
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (typeof value === 'string') {
    // A calendar date names a day, not a moment, so it is never shifted.
    // An impossible one (2026-13-45) is shown as sent, never rolled over into
    // a real-looking day.
    if (DATE_ONLY.test(value)) {
      return DateTime.fromISO(value).isValid
        ? formatDateToMMDDYYYY(value)
        : value;
    }
    if (DATE_TIME.test(value))
      return formatDateTimeInAgencyZone(value) || value;
    // Everything else, a masked SSN or MBI included, is shown as the server
    // sent it. The server owns masking; the client never re-masks or unmasks.
    return value;
  }
  if (Array.isArray(value)) {
    const items = value
      .map(formatScalar)
      .filter((item): item is string => item !== null);
    return items.length > 0 ? items.join('; ') : null;
  }
  if (typeof value === 'object') {
    const parts = Object.entries(value as Record<string, unknown>)
      .map(([key, inner]) => {
        const shown = formatScalar(inner);
        return shown === null ? null : `${humanizeField(key)}: ${shown}`;
      })
      .filter((part): part is string => part !== null);
    return parts.length > 0 ? parts.join(', ') : null;
  }
  return String(value);
};

/**
 * One field value as the log shows it, or `null` for an empty value (the
 * dialog renders that as "(empty)"). The record's own formatter wins.
 */
export const formatFieldValue = (
  field: string,
  value: unknown,
  formatValue?: WorkLogValueFormatter
): string | null => formatValue?.(field, value) ?? formatScalar(value);

/** One changed field, formatted for display. `null` is an empty value. */
export interface WorkLogShownChange {
  field: string;
  oldValue: string | null;
  newValue: string | null;
}

/**
 * The changes of an entry worth showing.
 *
 * A field that only moved between two spellings of empty (never set to an
 * empty list, say) reads "(empty) to (empty)" and tells the reader nothing,
 * so it is left out. A field whose two ends are the same stored value stays:
 * that is a field touched and put back, or one whose values are withheld, and
 * the log says it changed.
 */
export const shownChanges = (
  entry: WorkLogEntry,
  formatValue?: WorkLogValueFormatter
): WorkLogShownChange[] =>
  Object.entries(entry.what_was_edited ?? {})
    .map(([field, change]) => ({
      field,
      oldValue: formatFieldValue(field, change.old_value, formatValue),
      newValue: formatFieldValue(field, change.new_value, formatValue),
      sameStored:
        JSON.stringify(change.old_value ?? null) ===
        JSON.stringify(change.new_value ?? null),
    }))
    .filter(
      change =>
        change.oldValue !== null ||
        change.newValue !== null ||
        change.sameStored
    )
    .map(({ field, oldValue, newValue }) => ({ field, oldValue, newValue }));

/**
 * When an entry happened, in the agency's zone. A grouped entry spans a window,
 * so it shows the first and last times; on one agency day the date is not
 * repeated.
 */
export const formatEntryWhen = (entry: WorkLogEntry): string => {
  const last = formatDateTimeInAgencyZone(entry.edited_at);
  const first = entry.first_at;
  if ((entry.count ?? 1) <= 1 || !first || first === entry.edited_at) {
    return last;
  }
  if (
    formatDateInAgencyZone(first) === formatDateInAgencyZone(entry.edited_at)
  ) {
    return `${formatDateTimeInAgencyZone(first)} – ${formatTimeInAgencyZone(entry.edited_at)}`;
  }
  return `${formatDateTimeInAgencyZone(first)} – ${last}`;
};

/** "First Last", falling back to the email, then to an explicit Unknown. */
export const personName = (person: WorkLogEditor): string => {
  const { first_name, last_name, email } = person;
  const name = [first_name, last_name].filter(Boolean).join(' ').trim();
  return name || email || 'Unknown';
};

export type WorkLogActor =
  | { kind: 'person'; name: string }
  | {
      kind: 'system' | 'ai';
      source: string | null;
      initiator: string | null;
    };

/**
 * Who made an entry. A system or AI entry names the system that acted and,
 * when a person's action caused it, that person. A person's entry with no user
 * attached reads as System, as it always has.
 */
export const entryActor = (entry: WorkLogEntry): WorkLogActor => {
  if (entry.actor_type === 'human' && entry.editing_user) {
    return { kind: 'person', name: personName(entry.editing_user) };
  }
  return {
    kind: entry.actor_type === 'ai' ? 'ai' : 'system',
    source: entry.source || null,
    initiator: entry.initiated_by ? personName(entry.initiated_by) : null,
  };
};

/** "2026-09-28" -> "9/28/2026", read as a calendar day with no zone shift. */
export const formatTrackedSince = (day: string): string =>
  formatDateToMMDDYYYY(day);
